import classNames from 'classnames';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import useCategoryTree from '../../services/useCategoryTree';
import { CategoryHelper } from './utils';

export interface ProductProps {
  categoryTagId: string;
  name: string;
  sku: string;
}

export interface NavigationItem {
  href?: string;
  name: string;
}

export default function BreadcrumbNavigation({
  navigationItems,
  className,
}: {
  navigationItems: NavigationItem[];
  className?: string;
}) {
  return (
    <nav className={classNames('breadcrumb-navigation', className)}>
      <ol>
        {navigationItems.map((item, i) => (
          <li key={i}>{item.href ? <Link to={item.href}>{item.name}</Link> : <span>{item.name}</span>}</li>
        ))}
      </ol>
    </nav>
  );
}

export function CategoryBreadcrumbNavigation({
  categoryTagId,
  className,
}: {
  categoryTagId: string;
  className?: string;
}) {
  const { tree, error, isLoading } = useCategoryTree();

  const navigationItems = useMemo<NavigationItem[]>(() => {
    if (isLoading || error || !tree) {
      return [];
    }

    const helper = new CategoryHelper(tree);
    const category = helper.findCategory(categoryTagId);
    if (!category) {
      console.error(`Category not found: ${categoryTagId}`);

      return [];
    }

    const ancestors = helper.getAncestorCategories(category);

    // Remove the root category from the ancestors.
    if (ancestors.length >= 1) {
      ancestors.shift();
    }

    const navItems = [
      { href: '/', name: 'Home' },
      ...ancestors.map((item) => ({
        href: `/category/${item.categoryTagId}`,
        name: item.menuName,
      })),
      { href: undefined, name: category.menuName },
    ];

    return navItems;
  }, [tree, error, isLoading]);

  return <BreadcrumbNavigation navigationItems={navigationItems} className={className} />;
}

export function ProductBreadcrumbNavigation({ product }: { product: ProductProps }) {
  const { tree, error, isLoading } = useCategoryTree();

  const navigationItems = useMemo<NavigationItem[]>(() => {
    if (isLoading || error || !tree) {
      return [];
    }

    const helper = new CategoryHelper(tree);
    const category = helper.findCategory(product.categoryTagId);
    if (!category) {
      console.error(`Category not found: ${product.categoryTagId}`);

      return [];
    }

    const ancestors = helper.getAncestorCategories(category);

    // Remove the root category from the ancestors.
    if (ancestors.length >= 1) {
      ancestors.shift();
    }

    const navItems = [
      { href: '/', name: 'Home' },
      ...ancestors.map((item) => ({
        href: `/category/${item.categoryTagId}`,
        name: item.menuName,
      })),
      { href: undefined, name: product.name },
    ];

    return navItems;
  }, [tree, error, isLoading]);

  return <BreadcrumbNavigation navigationItems={navigationItems} />;
}
